import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tooltip
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import EditNoteIcon from "@mui/icons-material/EditNote"; // Quick Edit Icon
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { db } from "../../firebase";
import {
    collection,
    onSnapshot,
    deleteDoc,
    doc,
    updateDoc,
    query,
    orderBy,
    where
} from "firebase/firestore";

function Viewtrips() {
    const navigate = useNavigate();
    const [trips, setTrips] = useState([]);
    const [search, setSearch] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(200);

    // Quick Edit State
    const [editOpen, setEditOpen] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [editData, setEditData] = useState({});

    // Image Dialog State
    const [imageOpen, setImageOpen] = useState(false);
    const [selectedImages, setSelectedImages] = useState({ tarePhotos: [], grossPhotos: [] });
    
    const [selectedDate, setSelectedDate] = useState(
        new Date().toISOString().split("T")[0] // default = today
    );

    const handleViewImages = (trip) => {
        setSelectedImages({
            tarePhotos: trip.tarePhotos || [],
            grossPhotos: trip.grossPhotos || []
        });
        setImageOpen(true);
    };

    useEffect(() => {
        const start = new Date(selectedDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(selectedDate);
        end.setHours(23, 59, 59, 999);

        const q = query(
            collection(db, "trips"),
            where("status", "==", "Completed"),
            where("grossDate", ">=", start),
            where("grossDate", "<=", end),
            orderBy("challan", "asc")
        );

        const unsub = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                // REMOVE BOLDER
                .filter(trip =>
                    !trip.products?.some(p =>
                        (p.productName || "").toLowerCase().includes("bolder")
                    )
                );

            setTrips(data);
        });

        return () => unsub();
    }, [selectedDate]);


    // DELETE
    const handleDelete = async (id) => {
        Swal.fire({
            title: "Delete Trip?",
            text: "This record will be permanently deleted.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes Delete"
        }).then(async (result) => {
            if (result.isConfirmed) {
                await deleteDoc(doc(db, "trips", id));
                Swal.fire("Deleted!", "Trip removed successfully", "success");
            }
        });
    };


    // FORMAT DATE FOR INPUT FIELD (YYYY-MM-DDTHH:mm)
    const formatDateTimeForInput = (timestamp) => {
        if (!timestamp) return "";
        const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        if (isNaN(dateObj)) return "";
        
        // Adjust for local timezone offset
        const tzoffset = dateObj.getTimezoneOffset() * 60000;
        const localISOTime = new Date(dateObj - tzoffset).toISOString().slice(0, 16);
        return localISOTime; 
    };


    // OPEN QUICK EDIT DIALOG
    const handleQuickEdit = (trip) => {
        setSelectedTrip(trip.id);
        setEditData({
            challan: trip.challan || "",
            customerName: trip.customerName || trip.partyName || "", // fallback mapping just in case
            vehicleNo: trip.vehicleNo || "",
            grossDate: formatDateTimeForInput(trip.grossDate),
            tareDate: formatDateTimeForInput(trip.tareDate)
        });
        setEditOpen(true);
    };


    // SUBMIT QUICK UPDATE
    const handleUpdate = async () => {
        try {
            const updatePayload = {
                challan: editData.challan,
                customerName: editData.customerName,
                vehicleNo: editData.vehicleNo,
            };

            // Convert string back to proper JS Date for Firestore
            if (editData.grossDate) updatePayload.grossDate = new Date(editData.grossDate);
            if (editData.tareDate) updatePayload.tareDate = new Date(editData.tareDate);

            await updateDoc(doc(db, "trips", selectedTrip), updatePayload);

            Swal.fire("Updated", "Trip details updated successfully", "success");
            setEditOpen(false);

        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };


    // SEARCH FILTER
    const filteredTrips = trips.filter((trip) =>
        JSON.stringify(trip).toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Box sx={{
            bgcolor: '#f4f6f8',
            minHeight: '100vh',
            p: 1,
            "& .MuiInputBase-root": {
                fontSize: "14px"
            },
            "& .MuiTableCell-root": {
                fontSize: "13px",
                padding: "2px 2px"
            },
            "& .MuiButton-root": {
                fontSize: "11px",
                padding: "4px 8px"
            }
        }}>
            <Paper sx={{ p: 2 }}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Typography variant="h6">
                        Trips List
                    </Typography>

                    <TextField
                        type="date"
                        size="small"
                        label="Filter Date"
                        InputLabelProps={{ shrink: true }}
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                    />

                    <TextField
                        size="small"
                        label="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ background: "#f5f5f5" }}>
                                <TableCell>SR</TableCell>
                                <TableCell>Challan</TableCell>
                                <TableCell>Party</TableCell>
                                <TableCell>Vehicle</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell align="center">Qty</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Paid</TableCell>
                                <TableCell>Remaining</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTrips
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {

                                    const totalSale =
                                        row.products?.reduce(
                                            (sum, p) => sum + (Number(p.amount) || 0),
                                            0
                                        ) || 0;

                                    return (
                                        <TableRow key={row.id} hover>
                                            <TableCell>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>

                                            <TableCell>
                                                {row.challan || "-"}
                                            </TableCell>

                                            <TableCell>
                                                {row.customerName}
                                            </TableCell>

                                            <TableCell>
                                                {row.vehicleNo}
                                            </TableCell>

                                            <TableCell>
                                                {row.products?.map((p, i) => (
                                                    <Typography key={i} sx={{ fontSize: "0.8rem" }}>
                                                        {i + 1}) {p.productName}
                                                    </Typography>
                                                ))}
                                            </TableCell>

                                            <TableCell align="center">
                                                {row.products?.map((p, i) => (
                                                    <Typography key={i} sx={{ fontSize: "0.8rem" }}>
                                                        {p.qty}
                                                    </Typography>
                                                ))}
                                            </TableCell>

                                            <TableCell>
                                                <Typography fontWeight="bold">
                                                    ₹ {row.totalAmount || totalSale}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography color="green" fontWeight="bold">
                                                    ₹ {row.paidAmount || 0}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography
                                                    color={(row.remainingAmount || 0) > 0 ? "error" : "success"}
                                                    fontWeight="bold"
                                                >
                                                    ₹ {row.remainingAmount || 0}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="center">
                                                
                                                {/* Full Page Edit */}
                                                <Tooltip title="Full Edit">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => navigate(`/edit-trip/${row.id}`)}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>

                                                {/* Quick Popup Edit */}
                                                <Tooltip title="Quick Edit Dates & Details">
                                                    <IconButton
                                                        color="secondary"
                                                        onClick={() => handleQuickEdit(row)}
                                                    >
                                                        <EditNoteIcon />
                                                    </IconButton>
                                                </Tooltip>

                                                {/* Delete Button */}
                                                <Tooltip title="Delete Trip">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleDelete(row.id)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={filteredTrips.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>


            {/* --- QUICK EDIT DIALOG --- */}
            <Dialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Quick Edit Trip Details</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                    
                    <TextField
                        label="Challan No"
                        fullWidth
                        size="small"
                        value={editData.challan || ""}
                        onChange={(e) => setEditData({ ...editData, challan: e.target.value })}
                    />

                    <TextField
                        label="Customer Name"
                        fullWidth
                        size="small"
                        value={editData.customerName || ""}
                        onChange={(e) => setEditData({ ...editData, customerName: e.target.value })}
                    />

                    <TextField
                        label="Vehicle No"
                        fullWidth
                        size="small"
                        value={editData.vehicleNo || ""}
                        onChange={(e) => setEditData({ ...editData, vehicleNo: e.target.value })}
                    />

                    <TextField
                        label="Gross Date"
                        type="datetime-local"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={editData.grossDate || ""}
                        onChange={(e) => setEditData({ ...editData, grossDate: e.target.value })}
                    />

                    <TextField
                        label="Tare Date"
                        type="datetime-local"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={editData.tareDate || ""}
                        onChange={(e) => setEditData({ ...editData, tareDate: e.target.value })}
                    />

                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEditOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleUpdate}>
                        Save Updates
                    </Button>
                </DialogActions>
            </Dialog>


            {/* --- VIEW PHOTOS DIALOG --- */}
            <Dialog
                open={imageOpen}
                onClose={() => setImageOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Trip Photos</DialogTitle>
                <DialogContent>
                    <Typography fontWeight="bold" mt={1}>Tare Photos</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                        {selectedImages.tarePhotos.length > 0 ? (
                            selectedImages.tarePhotos.map((url, i) => (
                                <img
                                    key={i}
                                    src={url}
                                    alt={`Tare ${i}`}
                                    style={{ width: 250, height: 150, objectFit: "cover", borderRadius: 4, border: "1px solid #ccc" }}
                                />
                            ))
                        ) : (
                            <Typography variant="body2">No Tare photos available</Typography>
                        )}
                    </Box>

                    <Typography fontWeight="bold" mt={1}>Gross Photos</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {selectedImages.grossPhotos.length > 0 ? (
                            selectedImages.grossPhotos.map((url, i) => (
                                <img
                                    key={i}
                                    src={url}
                                    alt={`Gross ${i}`}
                                    style={{ width: 250, height: 150, objectFit: "cover", borderRadius: 4, border: "1px solid #ccc" }}
                                />
                            ))
                        ) : (
                            <Typography variant="body2">No Gross photos available</Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setImageOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default Viewtrips;